import React from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { FontFamily } from '../../../constants/fonts';
import { useTheme } from '../../../theme';
import SkeletonBlock from './SkeletonBlock';

interface MetricCardProps {
  title: string;
  value: string;
  subtitle?: string;
  description?: string;

  valueColor?: string;

  trendText?: string;
  trendDirection?: 'up' | 'down';
  trendImpact?: 'positive' | 'negative' | 'neutral' | null | undefined;

  badgeText?: string;

  /**
   * When true the card renders an animated shimmer placeholder instead of the
   * real content. Keeps the exact same card chrome / height so grids don't jump
   * once the API data arrives.
   */
  loading?: boolean;
}

/**
 * Shimmering placeholder shown while metric data is being fetched.
 * Lives inside MetricCard so any consumer gets the skeleton for free by
 * simply passing `loading`. Keeps the same card chrome (border / padding /
 * bar heights) so the grid doesn't shift once the real values arrive.
 *
 * The shimmer animation itself comes from the shared `SkeletonBlock` so the
 * pulse timing stays identical across every analytics component.
 */
const MetricCardSkeleton: React.FC<{ withDescription?: boolean }> = ({ withDescription = true }) => {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  return (
    <View
      style={[styles.card, isDark && { backgroundColor: '#202020', borderColor: '#404040' }]}
      accessibilityLabel="Loading metric"
      accessible
    >
      <SkeletonBlock style={[styles.skeletonTitle, isDark && { backgroundColor: '#363636' }]} />
      <SkeletonBlock style={[styles.skeletonValue, isDark && { backgroundColor: '#363636' }]} />
      {withDescription ? <SkeletonBlock style={[styles.skeletonDescription, isDark && { backgroundColor: '#363636' }]} /> : null}
    </View>
  );
};

const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  subtitle,
  description,
  valueColor = '#2D2F33',
  trendText,
  trendDirection,
  trendImpact,
  badgeText,
  loading = false,
}) => {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  if (loading) {
    return <MetricCardSkeleton />;
  }

  // In dark mode, shift the default value color (#2D2F33 is near-black → unreadable)
  // to white. Callers that pass an explicit valueColor (e.g. the aging-red) keep their
  // color; we only override the default.
  const resolvedValueColor =
    isDark && valueColor === '#2D2F33' ? '#F2F2F2' : valueColor;

  // Trend badge colours per impact × theme
  const trendBadgeDark: Record<string, { bg: string; fg: string }> = {
    positive: { bg: '#14301a', fg: '#4ade80' },
    negative: { bg: '#3D0A00', fg: '#FF8A80' },
    neutral:  { bg: '#2B2B2B', fg: '#B0B0B0' },
  };
  const darkTrend = isDark ? (trendBadgeDark[trendImpact ?? 'neutral'] ?? trendBadgeDark.neutral) : null;

  return (
    <View style={[styles.card, isDark && { backgroundColor: '#202020', borderColor: '#404040' }]}>
      <Text style={[styles.title, isDark && { color: '#B0B0B0' }]}>{title}</Text>

      <View style={styles.valueRow}>
        <Text style={[styles.value, { color: resolvedValueColor }]}>
          {value}
        </Text>

        {subtitle && (
          <Text style={[styles.subtitle, isDark && { color: '#B0B0B0' }]}>
            {subtitle}
          </Text>
        )}

        {badgeText && (
          <Text style={[styles.badge, isDark && { color: '#8A9BB0' }]}>
            {badgeText}
          </Text>
        )}

        {trendText && (
          <View
            style={[
              styles.trendBadge,
              !isDark && (
                trendImpact === 'positive'
                  ? styles.positive
                  : trendImpact === 'negative'
                  ? styles.negative
                  : styles.neutral
              ),
              isDark && darkTrend ? { backgroundColor: darkTrend.bg } : undefined,
            ]}
          >
            <Text style={[styles.trendArrow, isDark && darkTrend ? { color: darkTrend.fg } : undefined]}>
              {trendDirection === 'down' ? '▼' : '▲'}
            </Text>

            <Text style={[styles.trendText, isDark && darkTrend ? { color: darkTrend.fg } : undefined]}>
              {trendText}
            </Text>
          </View>
        )}
      </View>

      {!!description && (
        <Text style={[styles.description, isDark && { color: '#8A9BB0' }]}>
          {description}
        </Text>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E8EDF3',
    padding: 20,
    width: '100%',
    flex: 1,
    shadowColor: '#000000',
    shadowOpacity: 0.04,
    shadowRadius: 4,
    shadowOffset: {
      width: 0,
      height: 1,
    },
    elevation: 1,
  },

  title: {
    fontSize: 11,
    fontFamily: FontFamily.bold,
    color: '#6A6D71',
    textTransform: 'uppercase',
    letterSpacing: 0.2,
    marginBottom: 4,
  },

  valueRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    flexWrap: 'wrap',
    gap: 8,
  },

  value: {
    fontSize: 28,
    fontFamily: FontFamily.bold,
  },

  subtitle: {
    fontSize: 14,
    fontFamily: FontFamily.regular,
    color: '#6A6D71',
  },

  badge: {
    fontSize: 14,
    fontFamily: FontFamily.regular,
    color: '#9AA1AB',
  },

  trendBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 12,
    paddingHorizontal: 8,
    paddingVertical: 3,
    gap: 4,
  },

  positive: {
    backgroundColor: '#E7F4EA',
  },

  negative: {
    backgroundColor: '#FDECEC',
  },

  neutral: {
    backgroundColor: '#F3F5F7',
  },

  trendArrow: {
    fontSize: 10,
    fontFamily: FontFamily.bold,
  },

  trendText: {
    fontSize: 11,
    fontFamily: FontFamily.bold,
  },

  description: {
    marginTop: 2,
    fontSize: 12,
    fontFamily: FontFamily.regular,
    color: '#9AA1AB',
  },

  // ─── Skeleton ─────────────────────────────────────────────────────────────
  skeletonTitle: {
    height: 10,
    width: '62%',
    marginBottom: 12,
  },

  skeletonValue: {
    height: 26,
    width: '45%',
    marginBottom: 12,
  },

  skeletonDescription: {
    height: 8,
    width: '80%',
  },
});


export default MetricCard;